import { useEffect, useRef, useState } from 'react'

import { MenuIcon } from './Icons'
import { useEscape } from '../hooks/useEscape'

/**
 * **A menu ☰ with the name of the chosen entry beside it** (1.191.0), for
 * the new navigation: the functions of the workbench under the map and the
 * media of a trip in the head. From use: "Ausklapp-Menü statt fester Pille -
 * wie bei den Kartenoptionen". One component for both, so the two cannot
 * run apart.
 *
 * The list opens over what lies beside it (`opens`), takes taps, and closes
 * on a choice, on a tap elsewhere and on Escape (`useEscape`, the top layer
 * only). `name` gives the marks a run asks for: `data-{name}-menu` at the
 * button, `data-{name}-choice` at each entry.
 */
export function ChoiceMenu<K extends string>({
  name,
  label,
  choices,
  current,
  onChoose,
  opens,
}: {
  name: string
  label: string
  choices: { key: K; word: string }[]
  current: K
  onChoose: (key: K) => void
  opens: 'up' | 'down'
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement | null>(null)
  const currentWord = choices.find((c) => c.key === current)?.word ?? ''

  useEscape(open ? () => setOpen(false) : null)
  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent) => {
      if (event.target instanceof Node && box.current?.contains(event.target)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [open])

  return (
    <div className={`choice-menu choice-menu--${opens}`} ref={box}>
      <button
        type="button"
        className="glass-button choice-menu__open"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen(!open)}
        {...{ [`data-${name}-menu`]: '' }}
      >
        <MenuIcon />
      </button>
      <button
        type="button"
        className="choice-menu__chip"
        aria-hidden="true"
        tabIndex={-1}
        onClick={() => setOpen(!open)}
      >
        {currentWord}
      </button>
      {open && (
        <div className="choice-menu__list" role="menu" aria-label={label}>
          {choices.map((c) => (
            <button
              key={c.key}
              type="button"
              role="menuitemradio"
              aria-checked={c.key === current}
              className="choice-menu__choice"
              onClick={() => {
                setOpen(false)
                if (c.key !== current) onChoose(c.key)
              }}
              {...{ [`data-${name}-choice`]: c.key }}
            >
              {c.word}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
